import { Injectable } from '@angular/core';
import { Router, NavigationExtras } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { LocalStoreManager } from '../Common/local-store-manager.service';

import { ConfigurationService } from '../Common/configuration.service';
import { DBkeys } from '../db-Keys';
import { JwtHelper } from '../helpers/jwt-helper';
import { Utilities } from '../helpers/utilities';
import { LoginResponse, AccessToken } from '@app/shared/models/Authentication/login.model';
import { User, UserRoles, UserTenantMap } from '@app/shared/models/Authentication/user.model';
import { Permission, PermissionNames, PermissionValues, ServicePermissionValues } from '@app/shared/models/Authentication/permission.model';
import { AuthenticationEndpoint } from '@app/core/endpoints/authentication-endpoint.service';
import { HttpHeaders, HttpParams, HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { AppService, Tenant } from '@app/shared/models/Common/enums';



@Injectable()
export class AuthService {


  public get loginUrl() { return this.configurations.loginUrl; }
  public get homeUrl() { return this.configurations.homeUrl; }
  private readonly _loginUrl: string = `${this.configurations.authenticationUrl}/connect/token`;
  public loginRedirectUrl: string;
  public logoutRedirectUrl: string;

  public reLoginDelegate: () => void;

  private previousIsLoggedInCheck = false;
  private _loginStatus = new Subject<boolean>();


  constructor(
    private router: Router,
    private configurations: ConfigurationService,
    //private endpointFactory: EndpointFactory,
    private authEndPoint: AuthenticationEndpoint,
    private localStorage: LocalStoreManager,
    private http: HttpClient
  ) {
    this.initializeLoginStatus();
  }


  private initializeLoginStatus() {
    this.localStorage.getInitEvent().subscribe(() => {
      this.reevaluateLoginStatus();
    });
  }


  gotoPage(page: string, preserveParams = true) {

    const navigationExtras: NavigationExtras = {
      queryParamsHandling: preserveParams ? 'merge' : '', preserveFragment: preserveParams
    };


    this.router.navigate([page], navigationExtras);
  }


  redirectLoginUser() {
    const redirect = this.loginRedirectUrl && this.loginRedirectUrl != '/' && this.loginRedirectUrl != ConfigurationService.defaultHomeUrl ? this.loginRedirectUrl : this.homeUrl;
    this.loginRedirectUrl = null;


    const urlParamsAndFragment = Utilities.splitInTwo(redirect, '#');
    const urlAndParams = Utilities.splitInTwo(urlParamsAndFragment.firstPart, '?');

    const navigationExtras: NavigationExtras = {
      fragment: urlParamsAndFragment.secondPart,
      queryParams: Utilities.getQueryParamsFromString(urlAndParams.secondPart),
      queryParamsHandling: 'merge'
    };

    this.router.navigate([urlAndParams.firstPart], navigationExtras);
  }


  redirectLogoutUser() {
    const redirect = this.logoutRedirectUrl ? this.logoutRedirectUrl : this.loginUrl;
    this.logoutRedirectUrl = null;

    this.router.navigate([redirect]);
  }


  redirectForLogin() {
    this.loginRedirectUrl = this.router.url;
    this.router.navigate([this.loginUrl]);
  }

  redirectUnAuthorized() {
    this.router.navigate(['/unauthorized']);
  }


  reLogin() {

    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);

    if (this.reLoginDelegate) {
      this.reLoginDelegate();
    } else {
      this.redirectForLogin();
    }
  }


  refreshLogin() {
    return this.getRefreshLoginEndpoint<LoginResponse>().pipe(
      map(response => this.processLoginResponse(response, this.rememberMe)));
  }


  login(userName: string, password: string, rememberMe?: boolean, recaptcha?: string) {

    if (this.isLoggedIn) {
      this.logout();
    }

    return this.authEndPoint.getLoginEndpoint<LoginResponse>(userName, password, recaptcha).pipe(
      map(response => this.processLoginResponse(response, rememberMe)));
  }

  forgotPassword(obj: any): Observable<any> {
    return this.authEndPoint.forgotPasswordEndpoint<any>(obj);
  }
  validateResetPwdToken(userId: string, token: string): Observable<any> {
    return this.authEndPoint.validateResetPwdToken<any>(userId, token);
  }
  resetPassword(obj: any): Observable<any> {
    return this.authEndPoint.resetPasswordEndpoint<any>(obj);
  }
  processLoginResponse(response: LoginResponse, rememberMe: boolean) {

    const accessToken = response.access_token;

    if (accessToken == null) {
      throw new Error('Received accessToken was empty');
    }

    //const idToken = response.id_token;
    const refreshToken = response.refresh_token || this.refreshToken;
    const expiresIn = response.expires_in;
    const tokenExpiryDate = new Date();
    tokenExpiryDate.setSeconds(tokenExpiryDate.getSeconds() + expiresIn);

    const accessTokenExpiry = tokenExpiryDate;

    const jwtHelper = new JwtHelper();
    const decodedAccessToken = <AccessToken>jwtHelper.decodeToken(response.access_token);
    const permissions: PermissionValues[] = Array.isArray(decodedAccessToken.permission) ? decodedAccessToken.permission : [decodedAccessToken.permission];
    // const userRoles: UserRoles[] = [
    //   { serviceId: 1, roleId: "c5da89bf-ca18-4319-aa23-9a6053912013", roleName: "HEALTH REINSURER UNDERWRITER" },
    //   { serviceId: 2, roleId: "c5da89bf-ca18-4319-aa23-9a6053912014", roleName: "LIFE REINSURER UNDERWRITER" },
    //   { serviceId: 1, roleId: "c5da89bf-ca18-4319-aa23-9a6053912015", roleName: "HEALTH RI UW Manager" },
    //   { serviceId: 2, roleId: "c5da89bf-ca18-4319-aa23-9a6053912016", roleName: "LIFE RI UW Manager" },
    // ]

    // if (!this.isLoggedIn) {
    //   this.configurations.import(decodedAccessToken.configuration);
    // }

    const user = new User(
      decodedAccessToken.sub,
      decodedAccessToken.name,
      decodedAccessToken.fullname,
      decodedAccessToken.email,
      decodedAccessToken.jobtitle,
      decodedAccessToken.phone_number,
      Number(decodedAccessToken.entityid),
      decodedAccessToken.entityname,
      decodedAccessToken.entitytype,
      Number(decodedAccessToken.tenantid),
      // decodedAccessToken.tenantCode.toLowerCase(),
      // JSON.parse(decodedAccessToken.tenantmap),
      decodedAccessToken.currency,
      Array.isArray(decodedAccessToken.role) ? decodedAccessToken.role : [decodedAccessToken.role],
      decodedAccessToken.logo
    );
    user.isEnabled = true;

    this.saveUserDetails(user, permissions, accessToken, refreshToken, accessTokenExpiry, rememberMe);

    this.reevaluateLoginStatus(user);

    return user;
  }


  private saveUserDetails(user: User, permissions: PermissionValues[], accessToken: string, refreshToken: string, expiresIn: Date, rememberMe: boolean) {

    if (rememberMe) {
      this.localStorage.savePermanentData(accessToken, DBkeys.ACCESS_TOKEN);
      this.localStorage.savePermanentData(refreshToken, DBkeys.REFRESH_TOKEN);
      this.localStorage.savePermanentData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
      this.localStorage.savePermanentData(permissions, DBkeys.USER_PERMISSIONS);
      this.localStorage.savePermanentData(user, DBkeys.CURRENT_USER);
      this.localStorage.savePermanentData(user.tenantId, DBkeys.USER_CURRENT_TENANT);
      // this.localStorage.savePermanentData(userRoles, DBkeys.USER_ROLES);

    } else {
      this.localStorage.saveSyncedSessionData(accessToken, DBkeys.ACCESS_TOKEN);
      this.localStorage.saveSyncedSessionData(refreshToken, DBkeys.REFRESH_TOKEN);
      this.localStorage.saveSyncedSessionData(expiresIn, DBkeys.TOKEN_EXPIRES_IN);
      this.localStorage.saveSyncedSessionData(permissions, DBkeys.USER_PERMISSIONS);
      this.localStorage.saveSyncedSessionData(user, DBkeys.CURRENT_USER);
      this.localStorage.saveSyncedSessionData(user.tenantId, DBkeys.USER_CURRENT_TENANT);
      // this.localStorage.saveSyncedSessionData(userRoles, DBkeys.USER_ROLES);
    }

    this.localStorage.savePermanentData(rememberMe, DBkeys.REMEMBER_ME);
  }

  public userTenantMapSubject = new BehaviorSubject<UserTenantMap[]>([]);
  public userTenantMap$ = this.userTenantMapSubject.asObservable(); // Expose Observable

  saveUserTenantMap(userTenants: UserTenantMap[]) {

    this.localStorage.saveSyncedSessionData(userTenants, DBkeys.USER_TENANT_MAP);
    this.userTenantMapSubject.next(userTenants); // Notify subscribers

  }
  saveIndQuoteAccessFlag(hasAccess: boolean) {
    this.localStorage.localStorageSetItem(DBkeys.HAS_USER_IND_Access, hasAccess);
  }

  logout(): void {
    this.localStorage.clearAllStorage();

    this.localStorage.deleteData(DBkeys.ACCESS_TOKEN);
    this.localStorage.deleteData(DBkeys.REFRESH_TOKEN);
    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);
    this.localStorage.deleteData(DBkeys.USER_PERMISSIONS);
    this.localStorage.deleteData(DBkeys.CURRENT_USER);
    this.localStorage.deleteData(DBkeys.HAS_USER_IND_Access);
    this.configurations.clearLocalChanges();

    //this.reevaluateLoginStatus();
  }


  private reevaluateLoginStatus(currentUser?: User) {

    const user = currentUser || this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    const isLoggedIn = user != null;

    if (this.previousIsLoggedInCheck != isLoggedIn) {
      setTimeout(() => {
        this._loginStatus.next(isLoggedIn);
      });
    }

    this.previousIsLoggedInCheck = isLoggedIn;
  }


  getLoginStatusEvent(): Observable<boolean> {
    return this._loginStatus.asObservable();
  }

  getRefreshLoginEndpoint<T>(): Observable<T> {

    const header = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    const params = new HttpParams()
      .append('refresh_token', this.refreshToken)
      .append('x_client_id', environment.clientId)
      //.append('client_id', '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ')

      .append('grant_type', 'refresh_token')
      .append('scope', 'openid email phone profile offline_access roles');

    return this.http.post<T>(this._loginUrl, params, { headers: header });
  }


  get currentUser(): User {
    const user = this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER);
    this.reevaluateLoginStatus(user);
    return user;
  }
  get userTenantMapEvent(): UserTenantMap[] {
    return this.userTenantMapSubject.value;
  }

  get userTenantMap(): UserTenantMap[] {
    const userTenants = this.localStorage.getDataObject<UserTenantMap[]>(DBkeys.USER_TENANT_MAP);
    return userTenants;
  }

  get currentUserTenant(): number {
    const userTenant = this.localStorage.getDataObject<string>(DBkeys.USER_CURRENT_TENANT);
    return +userTenant;
  }
  get userPermissions(): ServicePermissionValues[] {
    return this.localStorage.getDataObject<ServicePermissionValues[]>(DBkeys.USER_PERMISSIONS) || [];
  }
  get userRoles(): UserRoles[] {
    return this.localStorage.getDataObject<UserRoles[]>(DBkeys.USER_ROLES) || [];
  }
  get accessToken(): string {

    this.reevaluateLoginStatus();
    return this.localStorage.getData(DBkeys.ACCESS_TOKEN);
  }
  get hasIndProductAccess(): boolean {
    return this.localStorage.getData(DBkeys.HAS_USER_IND_Access);
  }
  get accessTokenExpiryDate(): Date {

    this.reevaluateLoginStatus();
    return this.localStorage.getDataObject<Date>(DBkeys.TOKEN_EXPIRES_IN, true);
  }

  get isSessionExpired(): boolean {

    if (this.accessTokenExpiryDate == null) {
      return true;
    }

    return !(this.accessTokenExpiryDate.valueOf() > new Date().valueOf());
  }


  // get idToken(): string {

  //   this.reevaluateLoginStatus();
  //   return this.localStorage.getData(DBkeys.ID_TOKEN);
  // }

  get refreshToken(): string {

    this.reevaluateLoginStatus();
    return this.localStorage.getData(DBkeys.REFRESH_TOKEN);
  }

  get isLoggedIn(): boolean {
    return this.currentUser != null;
  }

  get rememberMe(): boolean {
    return this.localStorage.getDataObject<boolean>(DBkeys.REMEMBER_ME) == true;
  }
  get tenantId(): number {
    return this.currentUserTenant;
  }
  get apiVersion(): string {
    return this.currentUser.tenantId == Tenant.DN.id ? "2.0" : "1.0"
  }
  get currency(): string {
    return this.currentUser.currency;
  }
  get businessEntity(): any {
    return { id: this.currentUser.entityId, name: this.currentUser.entityName };
  }
  get entityType(): any {
    return this.currentUser?.entityType;
  }
  get isReInsurer(): any {
    return this.currentUser?.entityType === "REINSURER";
  }
  get isBroker(): any {
    return this.currentUser?.entityType === "BROKER";
  }
  get tenantCode(): string {
    // tslint:disable-next-line: forin
    for (const key in Tenant) {
      if (Tenant.hasOwnProperty(key)) {
        const element = Tenant[key];
        if (element.id === this.tenantId) {
          return String(element.Code).toLowerCase();
        }
      }
    }
    // return this.currentUser.tenantCode; test
  }
get tenantName(): string {
    // tslint:disable-next-line: forin
    for (const key in Tenant) {
      if (Tenant.hasOwnProperty(key)) {
        const element = Tenant[key];
        if (element.id === this.tenantId) {
          return this.userTenantMap.find(x=>x.tenantId == element.id).tenantName;
        }
      }
    }
    // return this.currentUser.tenantCode; test
  }
  get hasHealthAccess(): boolean {
    return this.userTenantMap?.some(f => f.tenantId == this.tenantId && f.serviceId == AppService.Health) || false;
  }
  get hasLifeAccess(): boolean {
    return this.userTenantMap?.some(f => f.tenantId === this.tenantId && f.serviceId === AppService.Life) || false;
  }
}
